// StructGob — the structure behavioral base: terrain occupation + gid shadowing, power
// supply/demand wiring, repair (credit drip), the being-built/idle/dying state machine
// with smoke + scorch spawns (SYNC RNG: counts, delays, positions, and the scorch
// size-rejection loop), and need-credits bookkeeping. SmokeGob rides along (Struct.cpp's
// second class). game/Struct.cpp (all 1,188 lines).
//
// Presentation note: the kfUnitDraw* symbol bits and kfGobLayerSymbols flash logic are
// machine-local (gated on gpplrLocal's perspective + handicap) and DELIBERATELY diverge
// across clients — they are excluded from the hashed FF mask. The flag MUTATIONS are
// ported because they share words with sim bits (kfUnitRepairing/kfUnitNeedCredits).

import type { SimContext } from '../context.ts';
import {
  Gob,
  kfGobStructure,
  kfGobActive,
  kfGobBeingBuilt,
  kfGobLayerSelection,
  kfGobLayerSymbols,
  kfGobLayerDepthSorted,
  kfGobLayerSurfaceDecal,
} from './gob.ts';
import { UnitGob } from './unitgob.ts';
import {
  StructConsts,
  kwcTileHalf,
  kfUntcLargeDefog,
  kfUntcNotifyPowerLowHigh,
} from './unitconsts.ts';
import {
  kfUnitRepairing,
  kfUnitDrawRepairingSymbol,
  kfUnitDrawNeedsPowerSymbol,
  kfUnitDrawNeedCreditsSymbol,
  kfUnitNeedCredits,
  kfPerfSymbolFlashing,
  kfPerfSmoke,
  kfPerfScorchMarks,
} from '../player/masks.ts';
import {
  kfHcapIncreasedArmor,
  kfHcapShowEnemyResourceStatus,
  knConsumerRepair,
  kfPlrStructureAttacked,
} from '../player/side.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  knHandled,
  knNotHandled,
  knDeleted,
  kstReservedGlobal,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidPowerLowHigh,
  kmidHit,
  kmidSpawnSmoke,
  kmidSelfDestructCommand,
  kmidRepairCommand,
  kmidBuildComplete,
  kmidDelete,
  kstBeingBuilt,
  kstIdle,
  kstDying,
} from '../sm/message.ts';
import { kfAniIgnoreFirstAdvance, kfAniLoop } from '../anim/animation.ts';
import { TcFromWc, WcFromTc, WcTrunc, WcFromUpc, WcFromTile16ths } from '../math/coords.ts';
import { itofx, mulfx, subfx, addfx } from '../math/fix.ts';
import { idiv } from '../math/int32.ts';
import { kbfStructure, kbfReserved } from '../terrain/terrainmap.ts';
import { g_mpDirToDx, g_mpDirToDy } from '../math/dir.ts';
import { knLimitSupport, knLimitScorch, knLimitStruct } from './gobmgr.ts';
import { AnimGob, kfAnmDeleteWhenDone, kfAnmSmokeFireLayer, kfAnmLoop } from './animgob.ts';
import { CreateAnimGob } from './animgob.ts';
import { ScorchGob, kScorchSizesPx } from './surfacedecal.ts';
import { kgtScorch, kgtSmoke } from './gobtypes.ts';
import { knDecreasedDamagePercent } from './mobileunit.ts';
import { FindNearestFreeTile } from './tilesearch.ts';
import type { WPoint, TRectLike } from '../types.ts';

// game/ht.h:6740-6743
export const kfxHealthUnitsRepairedPerUpdate = itofx(1);
export const knCreditsPerRepairUpdate = 1;
export const kcupdSymbolFlashRate = 8;

export class StructGob extends UnitGob {
  m_tLastSmoke = 0;
  m_nSeqLastVisible = 0; // FindGobs visibility tracking (ht.h:6723)

  // game/Struct.cpp:153-158
  constructor(ctx: SimContext, pstruc: StructConsts) {
    super(ctx, pstruc);
    this.m_ff = (this.m_ff | kfGobStructure) >>> 0;
  }

  get m_pstruc(): StructConsts {
    return this.m_puntc as StructConsts;
  }

  // game/Struct.cpp:164-199
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    if (!super.Init(wx, wy, pplr, fxHealth, ff)) return false;

    // Mark this structure's position occupied + reserved on the terrain map
    const ptrmap = this.ctx.trmap!;
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    ptrmap.SetFlags(tx, ty, this.m_pstruc.ctxTiles, this.m_pstruc.ctyTiles, kbfStructure);
    ptrmap.SetFlags(tx, ty, this.m_pstruc.ctxReserve, this.m_pstruc.ctyReserve, kbfReserved);

    // Shadow this structure's occupation in the gid map
    this.ctx.gobm.ShadowGob(this, tx, ty, this.m_pstruc.ctxTiles, this.m_pstruc.ctyTiles);

    // Struct.cpp:186-189 — redraw this part of the minimap
    {
      const trc = { left: 0, top: 0, right: 0, bottom: 0 };
      this.GetTileRect(trc);
      this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);
    }

    if (ff & kfGobBeingBuilt) {
      this.SetState(kstBeingBuilt);
    } else {
      this.Activate();
      this.SetState(kstIdle);
    }

    return true;
  }

  // game/Struct.cpp:201-217
  override Delete(): void {
    const ptrmap = this.ctx.trmap!;
    ptrmap.ClearFlags(
      TcFromWc(this.m_wx),
      TcFromWc(this.m_wy),
      this.m_pstruc.ctxTiles,
      this.m_pstruc.ctyTiles,
      kbfStructure,
    );
    ptrmap.ClearFlags(
      TcFromWc(this.m_wx),
      TcFromWc(this.m_wy),
      this.m_pstruc.ctxReserve,
      this.m_pstruc.ctyReserve,
      kbfReserved,
    );

    this.ctx.gobm.UnshadowGob(
      this,
      TcFromWc(this.m_wx),
      TcFromWc(this.m_wy),
      this.m_pstruc.ctxTiles,
      this.m_pstruc.ctyTiles,
    );

    super.Delete();
  }

  // game/Struct.cpp:322-336
  override Activate(): void {
    // Now this structure can influence global power
    this.m_pplr.AddPowerSupplyAndDemand(this.m_pstruc.nPowerSupply, this.m_pstruc.nPowerDemand);

    const amNew = this.ctx.gobm.CalcAreaMaskRect(
      TcFromWc(this.m_wx),
      TcFromWc(this.m_wy),
      this.m_pstruc.ctxTiles,
      this.m_pstruc.ctyTiles,
    );
    this.ctx.gobm.MoveGobBetweenAreas(this.m_gid, 0, amNew);

    super.Activate();
  }

  // game/Struct.cpp:338-360
  override Deactivate(): void {
    this.WaitingForCredits(false);
    this.m_wfUnit &= ~(kfUnitRepairing | kfUnitDrawRepairingSymbol | kfUnitDrawNeedsPowerSymbol);
    this.EnableOrDisableSymbolLayer();

    this.m_pplr.AddPowerSupplyAndDemand(-this.m_pstruc.nPowerSupply, -this.m_pstruc.nPowerDemand);

    // (confirmation-dialog takedown: forms UI)

    const amOld = this.ctx.gobm.CalcAreaMaskRect(
      TcFromWc(this.m_wx),
      TcFromWc(this.m_wy),
      this.m_pstruc.ctxTiles,
      this.m_pstruc.ctyTiles,
    );
    this.ctx.gobm.MoveGobBetweenAreas(this.m_gid, amOld, 0);

    super.Deactivate();
  }

  // game/Struct.cpp:362-368
  override GetTileRect(ptrc: TRectLike): void {
    ptrc.left = TcFromWc(this.m_wx);
    ptrc.top = TcFromWc(this.m_wy);
    ptrc.right = ptrc.left + this.m_pstruc.ctxTiles;
    ptrc.bottom = ptrc.top + this.m_pstruc.ctyTiles;
  }

  // game/Struct.cpp:370-376
  GetTilePaddedWRect(): { left: number; top: number; right: number; bottom: number } {
    const left = WcTrunc(this.m_wx);
    const top = WcTrunc(this.m_wy);
    return {
      left,
      top,
      right: left + WcFromTc(this.m_pstruc.ctxTiles),
      bottom: top + WcFromTc(this.m_pstruc.ctyTiles),
    };
  }

  // game/Struct.cpp:378-395
  IsAccessible(tx: number, ty: number): boolean {
    const ptrmap = this.ctx.trmap!;
    const { ctx: cxMap, cty: cyMap } = this.ctx.gobm.GetMapSize();
    for (let dir = 0; dir < 8; dir++) {
      const txT = tx + g_mpDirToDx[dir];
      const tyT = ty + g_mpDirToDy[dir];
      if (txT < 0 || txT >= cxMap || tyT < 0 || tyT >= cyMap) continue;
      if (!ptrmap.IsBlocked(txT, tyT, kbfStructure)) return true;
    }
    return false;
  }

  // game/Struct.cpp:397-438 — a terrain-accessible point on this structure
  override GetAttackPoint(pwpt: WPoint): void {
    const wptExtentX = WcFromTc(TcFromWc(this.m_wx) + this.m_pstruc.ctxTiles);
    const wptExtentY = WcFromTc(TcFromWc(this.m_wy) + this.m_pstruc.ctyTiles);

    pwpt.wx = WcTrunc(this.m_wx + idiv(wptExtentX - this.m_wx, 2)) + kwcTileHalf;
    pwpt.wy = WcTrunc(this.m_wy + idiv(wptExtentY - this.m_wy, 2)) + kwcTileHalf;

    const txTest = TcFromWc(pwpt.wx);
    const tyTest = TcFromWc(pwpt.wy);
    if (this.IsAccessible(txTest, tyTest)) return;

    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    for (let tyT = ty; tyT < ty + this.m_pstruc.ctyTiles; tyT++) {
      for (let txT = tx; txT < tx + this.m_pstruc.ctxTiles; txT++) {
        if (txT !== txTest && tyT !== tyTest) {
          if (this.IsAccessible(txT, tyT)) {
            pwpt.wx = WcFromTc(txT) + kwcTileHalf;
            pwpt.wy = WcFromTc(tyT) + kwcTileHalf;
            return;
          }
        }
      }
    }

    // Nothing "accessible" — find something close by that is
    const wpt = FindNearestFreeTile(this.ctx, TcFromWc(pwpt.wx), TcFromWc(pwpt.wy), kbfStructure);
    pwpt.wx = wpt.wx;
    pwpt.wy = wpt.wy;
  }

  // game/Struct.cpp:503-633
  override DefUpdate(): void {
    let cUpdates: number;
    let cupdUntilFlash: number;

    if (
      this.m_puntc.wf & kfUntcNotifyPowerLowHigh &&
      (this.m_pplr as Player).IsPowerLow() &&
      this.m_ff & kfGobActive
    ) {
      cUpdates = this.ctx.sim!.GetUpdateCount();
      cupdUntilFlash = kcupdSymbolFlashRate - (cUpdates % kcupdSymbolFlashRate);

      if (cupdUntilFlash === kcupdSymbolFlashRate) {
        // Low Power flashes ON for odd intervals
        if (idiv(cUpdates, kcupdSymbolFlashRate) & 1) this.m_wfUnit |= kfUnitDrawNeedsPowerSymbol;
        else this.m_wfUnit &= ~kfUnitDrawNeedsPowerSymbol;

        this.EnableOrDisableSymbolLayer();
        if (this.ctx.wfPerfOptions & kfPerfSymbolFlashing) this.MarkRedraw();
      }
      this.m_unvl.MinSkip(cupdUntilFlash - 1);
    }

    // If repairing and sufficient credits are available
    if (this.m_wfUnit & kfUnitRepairing) {
      cUpdates = this.ctx.sim!.GetUpdateCount();
      cupdUntilFlash = kcupdSymbolFlashRate - (cUpdates % kcupdSymbolFlashRate);

      const nCreditsHave = (this.m_pplr as Player).GetCredits();

      if (cupdUntilFlash === kcupdSymbolFlashRate) {
        if (nCreditsHave > 0) {
          // Repair flashes OFF for odd intervals
          if (idiv(cUpdates, kcupdSymbolFlashRate) & 1) this.m_wfUnit &= ~kfUnitDrawRepairingSymbol;
          else this.m_wfUnit |= kfUnitDrawRepairingSymbol;
        } else {
          this.m_wfUnit &= ~kfUnitDrawRepairingSymbol;
        }
        this.EnableOrDisableSymbolLayer();
        if (this.ctx.wfPerfOptions & kfPerfSymbolFlashing) this.MarkRedraw();
      }

      const fxArmorStrength = this.m_pstruc.GetArmorStrength();
      if (this.m_fxHealth < fxArmorStrength) {
        let nCreditsNeeded = knCreditsPerRepairUpdate;

        // Take whatever is left (repair on a discount!)
        if (nCreditsHave !== 0 && nCreditsHave < nCreditsNeeded) nCreditsNeeded = nCreditsHave;

        if (nCreditsHave >= nCreditsNeeded) {
          let fxHealth = addfx(this.m_fxHealth, kfxHealthUnitsRepairedPerUpdate);
          (this.m_pplr as Player).SetCredits(nCreditsHave - nCreditsNeeded, true, knConsumerRepair);

          this.WaitingForCredits(false);

          if (fxHealth >= fxArmorStrength) {
            fxHealth = fxArmorStrength;
            this.m_wfUnit &= ~(kfUnitRepairing | kfUnitDrawRepairingSymbol);
            this.EnableOrDisableSymbolLayer();
            this.MarkRedraw();
          }
          this.SetHealth(fxHealth);
        } else {
          this.WaitingForCredits(true);
        }
      } else {
        this.m_wfUnit &= ~(kfUnitRepairing | kfUnitDrawRepairingSymbol);
        this.EnableOrDisableSymbolLayer();
        this.MarkRedraw();
      }

      this.m_unvl.MinSkip();
    }

    if (this.m_wfUnit & kfUnitNeedCredits) {
      cUpdates = this.ctx.sim!.GetUpdateCount();
      cupdUntilFlash = kcupdSymbolFlashRate - (cUpdates % kcupdSymbolFlashRate);

      if (cupdUntilFlash === kcupdSymbolFlashRate) {
        // NeedCredits flashes OFF for odd intervals
        if (idiv(cUpdates, kcupdSymbolFlashRate) & 1) this.m_wfUnit &= ~kfUnitDrawNeedCreditsSymbol;
        else this.m_wfUnit |= kfUnitDrawNeedCreditsSymbol;

        this.EnableOrDisableSymbolLayer();
        if (this.ctx.wfPerfOptions & kfPerfSymbolFlashing) this.MarkRedraw();
      }

      this.m_unvl.MinSkip();
    }

    super.DefUpdate();
  }

  // game/Struct.cpp:635-654 — machine-local symbol visibility (gpplrLocal perspective).
  // The mutated bits are draw-only and excluded from the hashed FF mask; a headless
  // re-sim with no local player skips the local-perspective filter.
  EnableOrDisableSymbolLayer(): void {
    const ffOld = this.m_ff;
    const plrLocal = this.ctx.plrLocal;

    if (
      plrLocal !== null &&
      !this.IsAlly(plrLocal.GetSide()) &&
      (plrLocal.GetHandicap() & kfHcapShowEnemyResourceStatus) === 0
    ) {
      this.m_wfUnit &= ~(kfUnitDrawNeedsPowerSymbol | kfUnitDrawNeedCreditsSymbol);
    }

    if (
      this.m_wfUnit &
      (kfUnitDrawRepairingSymbol | kfUnitDrawNeedsPowerSymbol | kfUnitDrawNeedCreditsSymbol)
    ) {
      this.m_ff = (this.m_ff | kfGobLayerSymbols) >>> 0;
    } else {
      this.m_ff = (this.m_ff & ~kfGobLayerSymbols) >>> 0;
    }

    if ((ffOld ^ this.m_ff) & kfGobLayerSymbols) {
      if (this.ctx.wfPerfOptions & kfPerfSymbolFlashing) this.MarkRedraw();
    }
  }

  // game/Struct.cpp:656-659
  NeedsRepair(): boolean {
    return this.m_fxHealth < this.m_pstruc.GetArmorStrength();
  }

  // game/Struct.cpp:661-674
  IsTakeoverable(pplr: Player): boolean {
    if (!this.ctx.gobm.IsBelowLimit(knLimitStruct, pplr)) {
      // (local-player alert: presentation)
      return false;
    }
    return (this.m_ff & kfGobActive) !== 0;
  }

  // game/Struct.cpp:676-716
  Takeover(pplr: Player): void {
    this.Deactivate();

    // replay/analytics seam (fire-and-forget, NON-C++): report the capture with the
    // pre-SetOwner side so the event carries who lost the structure.
    {
      const wpt = { wx: 0, wy: 0 };
      this.GetCenter(wpt);
      this.ctx.host.NotifyStructTakeover(
        this.m_pplr.GetSide(),
        pplr.GetSide(),
        this.GetUnitType(),
        wpt.wx,
        wpt.wy,
      );
    }

    this.ctx.gobm.TrackGobCounts(this, false);
    (this.m_pplr as Player).IncStructuresLost();
    this.SetOwner(pplr);
    (this.m_pplr as Player).IncEnemyStructuresKilled();
    this.ctx.gobm.TrackGobCounts(this, true);

    this.Activate();

    // Fog reveal for the LOCAL player (per-client, unhashed). Struct.cpp:695-704:
    // center tile, pattern by kfUntcLargeDefog (NOT always large).
    if (pplr === this.ctx.plrLocal) {
      const wpt = { wx: 0, wy: 0 };
      this.GetCenter(wpt);
      this.ctx.host.RevealFog(
        this,
        (this.m_pstruc.wf & kfUntcLargeDefog) !== 0,
        TcFromWc(wpt.wx),
        TcFromWc(wpt.wy),
      );
    }

    // Force an update so it syncs on lowpower/credits etc
    this.m_unvl.MinSkip();

    // Struct.cpp:710-715 — redraw this part of the minimap
    const trc = { left: 0, top: 0, right: 0, bottom: 0 };
    this.GetTileRect(trc);
    this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);
  }

  // game/Struct.cpp:720-747
  // game/Struct.cpp:221-243 (knVerStructGobState 3) — m_tLastSmoke round-trips as a
  // tick DELTA; the shadow + need-credits side effects replay after the base load
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 3) return false;
    const ctLastSmoke = pstm.ReadDword();
    this.m_tLastSmoke = this.ctx.sim!.GetTickCount() - ctLastSmoke;

    if (super.LoadState(pstm)) {
      this.ctx.gobm.ShadowGob(
        this,
        TcFromWc(this.m_wx),
        TcFromWc(this.m_wy),
        this.m_pstruc.ctxTiles,
        this.m_pstruc.ctyTiles,
      );
      if (this.m_wfUnit & kfUnitNeedCredits) this.WaitingForCredits(true, true);
      return true;
    }
    return false;
  }

  // game/Struct.cpp:245-250
  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(3);
    pstm.WriteDword(this.ctx.sim!.GetTickCount() - this.m_tLastSmoke);
    return super.SaveState(pstm);
  }

  WaitingForCredits(fNeed: boolean, fOverride: boolean = false, pplr: Player | null = null): void {
    if (fNeed === ((this.m_wfUnit & kfUnitNeedCredits) === kfUnitNeedCredits) && !fOverride) return;

    let cDelta: number;
    if (fNeed) {
      this.m_wfUnit |= kfUnitNeedCredits;
      cDelta = 1;
    } else {
      this.m_wfUnit &= ~(kfUnitDrawNeedCreditsSymbol | kfUnitNeedCredits);
      this.EnableOrDisableSymbolLayer();
      if (this.ctx.wfPerfOptions & kfPerfSymbolFlashing) this.MarkRedraw();
      cDelta = -1;
    }

    if (pplr === null) pplr = this.m_pplr as Player;
    pplr.ModifyNeedCreditsCount(cDelta);
  }

  // game/Struct.cpp:749-761
  OnPowerLowHigh(): void {
    if (!(this.GetOwner() as Player).IsPowerLow()) {
      this.m_wfUnit &= ~kfUnitDrawNeedsPowerSymbol;
      this.EnableOrDisableSymbolLayer();
      this.MarkRedraw();
    } else {
      // Force an update to start symbol flashing
      this.m_unvl.MinSkip();
    }
  }

  // game/Struct.cpp:763-788
  Repair(fOn: boolean): void {
    if (fOn) {
      if (this.m_fxHealth === this.m_pstruc.GetArmorStrength()) return;

      this.m_wfUnit |= kfUnitRepairing;

      if (this.m_pplr === this.ctx.plrLocal) {
        if ((this.m_pplr as Player).GetCredits() > 0)
          this.ctx.host.PlaySfx(this.m_pstruc.sfxRepair);
      }
    } else {
      this.m_wfUnit &= ~(kfUnitRepairing | kfUnitDrawRepairingSymbol | kfUnitDrawNeedCreditsSymbol);
      this.WaitingForCredits(false);
      if (this.m_pplr === this.ctx.plrLocal) this.ctx.host.PlaySfx(this.m_pstruc.sfxAbortRepair);
    }

    this.m_unvl.MinSkip();
  }

  // game/Struct.cpp:790-795 — most structures cannot attack; Towers override
  override IsValidTarget(_pgobTarget: Gob): boolean {
    return false;
  }

  // game/Struct.cpp:825-837
  SelfDestruct(fRecycleValue: boolean = true): void {
    if (fRecycleValue) {
      (this.m_pplr as Player).SetCredits(
        (this.m_pplr as Player).GetCredits() + idiv(this.m_pstruc.GetCost(), 2),
        true,
      );
      (this.m_pplr as Player).IncStructuresLost();
    }
    this.SetState(kstDying);
  }

  // game/Struct.cpp:839-853
  override SetHealth(fxHealth: number): void {
    super.SetHealth(fxHealth);

    if (this.m_ff & kfGobBeingBuilt) return;

    if (this.m_fxHealth * 2 < this.m_pstruc.GetArmorStrength()) {
      if (this.m_ani.GetStrip() !== 1) this.StartAnimation(this.m_ani, 1, 0, this.m_ani.GetFlags());
    } else {
      if (this.m_ani.GetStrip() !== 0) this.StartAnimation(this.m_ani, 0, 0, this.m_ani.GetFlags());
    }
  }

  // game/Struct.cpp:855-882
  private GetScorchGob(): ScorchGob {
    if (this.ctx.gobm.IsBelowLimit(knLimitScorch)) return new ScorchGob(this.ctx);

    // Find the oldest scorch and evict it
    let nSequenceOldest = 0x7fff;
    let pgobScorchOldest: ScorchGob | null = null;
    for (
      let pgobT = this.ctx.gobm.GetFirstGob();
      pgobT !== null;
      pgobT = this.ctx.gobm.GetNextGob(pgobT)
    ) {
      if (pgobT.GetType() !== kgtScorch) continue;
      const pgobScorch = pgobT as ScorchGob;
      const nSequence = pgobScorch.GetSequence();
      if (nSequence < nSequenceOldest) {
        nSequenceOldest = nSequence;
        pgobScorchOldest = pgobScorch;
      }
    }

    if (pgobScorchOldest !== null) {
      this.ctx.gobm.RemoveGob(pgobScorchOldest);
    }

    return new ScorchGob(this.ctx);
  }

  // game/Struct.cpp:884-1122
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidPowerLowHigh:
          this.OnPowerLowHigh();
          return knHandled;

        case kmidHit: {
          const fxHealthPrev = this.m_fxHealth;

          let fxDamage = itofx(pmsg.Hit.nDamage);
          if (this.m_pplr.GetHandicap() & kfHcapIncreasedArmor) {
            fxDamage = mulfx(fxDamage, idiv(itofx(knDecreasedDamagePercent), 100));
          }
          this.SetHealth(subfx(this.m_fxHealth, fxDamage));
          if (this.m_fxHealth <= 0) {
            const pplr = this.ctx.plrm.GetPlayer(pmsg.Hit.sideAssailant)!;
            pplr.IncEnemyStructuresKilled();
            (this.m_pplr as Player).IncStructuresLost();
            this.SetState(kstDying);
          } else {
            this.ShowDamageIndicator();

            // Dipping into the severely-damaged zone fires off some smoke
            if (
              fxHealthPrev * 2 >= this.m_pstruc.GetArmorStrength() &&
              this.m_fxHealth * 2 < this.m_pstruc.GetArmorStrength()
            ) {
              this.ctx.host.PlaySfx(this.m_pstruc.sfxDamaged);

              if (this.ctx.sim!.GetTickCount() - this.m_tLastSmoke > 200) {
                let cSmokes = (this.ctx.rng.GetRandom() % 3) + 2; // SYNC RNG
                if (this.m_pstruc.ctxTiles === 1 || this.m_pstruc.ctyTiles === 1) {
                  cSmokes = idiv(cSmokes, 2);
                }
                for (let i = 0; i < cSmokes; i++) {
                  this.ctx.smm!.SendDelayedMsgIds(
                    kmidSpawnSmoke,
                    this.ctx.rng.GetRandom() & 63,
                    this.m_gid,
                    this.m_gid,
                  ); // SYNC RNG
                }

                this.m_tLastSmoke = this.ctx.sim!.GetTickCount();
              }
            }
          }

          // Remember that a structure has been attacked (machine-local notify flag —
          // cleared by the Simulation notify seam)
          this.m_pplr.SetFlags(this.m_pplr.GetFlags() | kfPlrStructureAttacked);

          this.NotifyNearbyAlliesOfHit(pmsg.Hit.gidAssailant);
          return knHandled;
        }

        case kmidSpawnSmoke:
          if (this.ctx.wfPerfOptions & kfPerfSmoke) {
            if (this.ctx.gobm.IsBelowLimit(knLimitSupport)) {
              const pgob = new SmokeGob(this.ctx, this.ctx.rng.GetRandom() & 1); // SYNC RNG
              const wcx = WcFromTc(this.m_pstruc.ctxTiles);
              const wcy = WcFromTc(this.m_pstruc.ctyTiles) - kwcTileHalf;
              const wdx = this.ctx.rng.GetRandom() % wcx; // SYNC RNG
              const wdy = (this.ctx.rng.GetRandom() % wcy) + WcFromTile16ths(12); // SYNC RNG
              let wxSmoke = this.m_wx + wdx;
              let wySmoke = this.m_wy + wdy;

              const { ctx: cxMap, cty: cyMap } = this.ctx.gobm.GetMapSize();
              const wcxMap = WcFromTc(cxMap);
              const wcyMap = WcFromTc(cyMap);

              if (wxSmoke < 0) wxSmoke = 0;
              if (wxSmoke >= wcxMap) wxSmoke = wcxMap - 1;
              if (wySmoke >= wcyMap) wySmoke = wcyMap - 1;

              pgob.InitSmoke(wxSmoke, wySmoke, this.m_gid);
            }
          }
          return knHandled;

        case kmidSelfDestructCommand:
          this.SelfDestruct();
          return knHandled;

        case kmidRepairCommand:
          this.Repair(!(this.m_wfUnit & kfUnitRepairing));
          return knHandled;

        case kmidDelete:
          return knHandled; // C++ asserts (only valid in kstDying)
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstBeingBuilt) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_ff = (this.m_ff | kfGobLayerSelection) >>> 0;
          return knHandled;

        case kmidReservedExit:
          this.m_ff = (this.m_ff & ~kfGobLayerSelection) >>> 0;
          return knHandled;

        case kmidBuildComplete:
          this.Activate();
          this.SetState(kstIdle);
          return knHandled;

        case kmidReservedUpdate:
          // Don't advance animation
          this.DefUpdate();
          return knHandled;
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstIdle) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          // Idle animation strip chosen by health
          let nStrip: number;
          if (this.m_fxHealth === 0)
            nStrip = 2; // destroyed
          else if (this.m_fxHealth * 2 < this.m_pstruc.GetArmorStrength())
            nStrip = 1; // damaged
          else nStrip = 0; // healthy
          this.StartAnimation(this.m_ani, nStrip, 0, kfAniIgnoreFirstAdvance | kfAniLoop);
          return knHandled;
        }

        case kmidReservedUpdate:
          this.AdvanceAnimation(this.m_ani);
          this.DefUpdate();
          return knHandled;
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstDying) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          // Destroyed during building can land here unactivated
          if (this.m_ff & kfGobActive) this.Deactivate();
          this.m_ff = (this.m_ff ^ (kfGobLayerDepthSorted | kfGobLayerSurfaceDecal)) >>> 0;

          this.ctx.host.PlaySfx(this.m_pstruc.sfxDestroyed);
          this.StartAnimation(this.m_ani, 2, 0, kfAniIgnoreFirstAdvance);

          // Stay on the "occupied" map until it's all over (troops can't run across
          // this freshly destroyed area while it's hot)
          this.ctx.smm!.SendDelayedMsgIds(kmidDelete, 800, this.m_gid, this.m_gid);

          // A huge explosion
          const wcx = WcFromTc(this.m_pstruc.ctxTiles);
          const wcy = WcFromTc(this.m_pstruc.ctyTiles) + kwcTileHalf;
          const pgobExpl = CreateAnimGob(
            this.ctx,
            this.m_wx + idiv(wcx, 2),
            this.m_wy + idiv(wcy, 2),
            kfAnmDeleteWhenDone | kfAnmSmokeFireLayer,
            this.ctx.animDataFor('sexplosion.anir'),
          );
          if (pgobExpl !== null) pgobExpl.SetOwner(this.m_pplr);

          // Some smoke, in the near future
          const cSmokes = (this.ctx.rng.GetRandom() % 3) + 1; // SYNC RNG
          for (let i = 0; i < cSmokes; i++) {
            this.ctx.smm!.SendDelayedMsgIds(
              kmidSpawnSmoke,
              this.ctx.rng.GetRandom() & 63,
              this.m_gid,
              this.m_gid,
            ); // SYNC RNG
          }

          // A random smattering of scorch marks (perf flag pinned ON in MP)
          if (this.ctx.wfPerfOptions & kfPerfScorchMarks) {
            const cScorches = (this.ctx.rng.GetRandom() & 3) + 1; // SYNC RNG
            const wcxScorchable = WcFromTc(this.m_pstruc.ctxTiles);
            const wcyScorchable = WcFromTc(this.m_pstruc.ctyTiles);

            const { ctx: cxMap, cty: cyMap } = this.ctx.gobm.GetMapSize();
            const wcxMap = WcFromTc(cxMap);
            const wcyMap = WcFromTc(cyMap);

            for (let i = 0; i < cScorches; i++) {
              // BUG-COMPAT (sim-quirks A7): pick a random scorch SMALLER than the structure: each rejected pick
              // consumes a sync-RNG draw, so the scorch art SIZES are sim data
              let nScorch: number;
              let wcxScorch: number;
              let wcyScorch: number;
              do {
                nScorch = this.ctx.rng.GetRandom() % kScorchSizesPx.length; // SYNC RNG
                wcxScorch = WcFromUpc(kScorchSizesPx[nScorch].cx);
                wcyScorch = WcFromUpc(kScorchSizesPx[nScorch].cy);
              } while (wcxScorch > wcxScorchable || wcyScorch > wcyScorchable);

              const wxoff = this.ctx.rng.GetRandom() % (wcxScorchable - wcxScorch + 1); // SYNC RNG
              const wyoff = this.ctx.rng.GetRandom() % (wcyScorchable - wcyScorch + 1); // SYNC RNG

              const pgobScorch = this.GetScorchGob();
              let wxScorch = this.m_wx + wxoff + idiv(wcxScorch, 2);
              let wyScorch = this.m_wy + wyoff + wcyScorch;
              if (wxScorch < 0) wxScorch = 0;
              if (wxScorch >= wcxMap) wxScorch = wcxMap - 1;
              if (wyScorch >= wcyMap) wyScorch = wcyMap - 1;

              pgobScorch.InitScorch(wxScorch, wyScorch, nScorch);
            }
          }
          return knHandled;
        }

        case kmidReservedUpdate:
          this.AdvanceAnimation(this.m_ani);
          return knHandled;

        case kmidDelete:
          this.Delete();
          return knDeleted;

        case kmidSpawnSmoke:
          return knNotHandled; // bubble up to the global handler

        default:
          return knHandled; // DiscardMsgs (kmidHit, kmidNearbyAllyHit, ...)
      }
    }

    return knNotHandled;
  }
}

// game/Struct.cpp:1128-1186 — looping smoke: grow strip once, then cycle strip 1
// m_cLoops more times, then self-delete (the gid release timing is anir-data-driven).
export class SmokeGob extends AnimGob {
  m_cLoops: number;

  // game/Struct.cpp:1161-1166 — AnimGob is not savable; Smoke overrides back to savable
  override IsSavable(): boolean {
    return true;
  }

  constructor(ctx: SimContext, cLoops: number) {
    super(ctx);
    this.m_cLoops = cLoops;
  }

  InitSmoke(wx: number, wy: number, smidNotify: number): boolean {
    return this.Init(
      wx,
      wy,
      kfAnmLoop | kfAnmSmokeFireLayer,
      this.ctx.animDataFor('smoke.anir'),
      0,
      smidNotify,
    );
  }

  // game/Struct.cpp:1140-1159 (knVerSmokeGobState 1)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_cLoops = (pstm.ReadWord() << 16) >> 16;
    this.m_ani.Init(this.ctx.animDataFor('smoke.anir'));
    this.m_ani.LoadState(pstm);
    this.m_smidNotify = pstm.ReadWord();
    this.m_wfAnm = kfAnmLoop | kfAnmSmokeFireLayer;
    return super.LoadState(pstm); // AnimGob has no serializer of its own: Gob's runs
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteWord(this.m_cLoops);
    this.m_ani.SaveState(pstm);
    pstm.WriteWord(this.m_smidNotify);
    return super.SaveState(pstm);
  }

  override GetType(): number {
    return kgtSmoke;
  } // Struct.cpp:1168-1171

  override OnStripDone(): boolean {
    if (this.m_ani.GetStrip() === 0) {
      this.SetAnimationStrip(this.m_ani, 1);
    } else {
      this.m_cLoops--;
      if (this.m_cLoops < 0) return true;
    }
    return false;
  }
}

// self-register for savegame loading (GameObjects.cpp:78 CreateGob: `new SmokeGob(0)`)
import { registerGobType } from './factory.ts';
registerGobType(kgtSmoke, (ctx) => new SmokeGob(ctx, 0));
